// components/settings/SuggestionsPanel.jsx — Anamoria SPA
// v1.0 — Suggestions panel: submit + view past suggestions (May 23, 2026)
//
// Renders inside SettingsPage right panel. Provides a form (via RequestPanel
// with suggestion type hardcoded, picklist hidden) and a list of the user's
// past suggestions fetched from GET /account-requests/me.
//
// Props:
//   getApi    — API client factory
//   appState  — user context (passed through to RequestPanel)

import { useState, useEffect, useRef, useCallback } from 'react';
import RequestPanel from './RequestPanel';
import styles from './SuggestionsPanel.module.css';
import shared from './settingsShared.module.css';

export default function SuggestionsPanel({ getApi, appState }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submittedTimerRef = useRef(null);

  // ─── Fetch past suggestions on mount ───
  const loadSuggestions = useCallback(async () => {
    try {
      const api = getApi();
      const data = await api.get('/account-requests/me');
      const filtered = (data.requests || []).filter(
        r => r.requestType === 'suggestion'
      );
      setSuggestions(filtered);
      setFetchError(false);
    } catch (err) {
      console.error('SuggestionsPanel: fetch failed:', err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [getApi]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (submittedTimerRef.current) clearTimeout(submittedTimerRef.current);
    };
  }, []);

  // ─── Success callback: show banner, refresh list ───
  function handleSuccess() {
    setSubmitted(true);
    submittedTimerRef.current = setTimeout(() => setSubmitted(false), 4000);
    loadSuggestions();
  }

  // ─── Helper: display title for a suggestion row ───
  function getSuggestionTitle(suggestion) {
    if (suggestion.metadata?.subject) return suggestion.metadata.subject;
    if (suggestion.metadata?.message) {
      const msg = suggestion.metadata.message;
      return msg.length > 60 ? msg.substring(0, 60) + '…' : msg;
    }
    return 'Suggestion';
  }

  // ─── Helper: format date ───
  function formatDate(isoString) {
    if (!isoString) return '';
    try {
      return new Date(isoString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch { return ''; }
  }

  // ─── Helper: status badge class ───
  function getStatusBadgeClass(status) {
    switch (status) {
      case 'pending': return shared.badgePending;
      case 'in_progress': return styles.badgeInProgress;
      case 'completed': return styles.badgeCompleted;
      case 'cancelled': return styles.badgeCancelled;
      default: return shared.badgePending;
    }
  }

  function getStatusLabel(status) {
    switch (status) {
      case 'pending': return 'Pending';
      case 'in_progress': return 'In progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  return (
    <>
      <h2 className={styles.panelTitle}>Suggestions</h2>
      <p className={styles.panelHint}>
        Share ideas to help us improve Anamoria
      </p>

      {submitted && (
        <div className={styles.submittedBanner} role="status" aria-live="polite">
          Suggestion submitted — thank you!
        </div>
      )}

      <RequestPanel
        initialType="suggestion"
        hideTypePicklist
        getApi={getApi}
        appState={appState}
        onSuccess={handleSuccess}
        onCancel={null}
      />

      <hr className={styles.divider} />

      <h3 className={styles.suggestionsListTitle}>Your Suggestions</h3>

      {loading && (
        <p className={styles.loading}>Loading…</p>
      )}

      {!loading && !fetchError && suggestions.length === 0 && (
        <p className={styles.empty}>
          No suggestions yet — share your first idea above
        </p>
      )}

      {!loading && !fetchError && suggestions.length > 0 && (
        <div className={styles.suggestionsList}>
          {suggestions.map(s => (
            <div key={s.requestId} className={styles.suggestionRow}>
              <span className={styles.suggestionSubject}>
                {getSuggestionTitle(s)}
              </span>
              <div className={styles.suggestionMeta}>
                <span>{formatDate(s.requestedAt)}</span>
                <span className={getStatusBadgeClass(s.status)}>
                  {getStatusLabel(s.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && fetchError && (
        <p className={styles.empty}>
          Could not load past suggestions.
        </p>
      )}
    </>
  );
}
